import { getNoBreakCompensationPauseHours, getLogbookEntryTypeRule } from '../../config/logbookEntryRules.js';
import { getResponsibleQuestion, getTimeQuestion } from '../../config/logbookEntryWizardSchema.js';
import { resolveActivityTypeConfig } from '../../config/activityTypeConfig.js';
import { createEmptyActivityTypeDraft } from './activityTypeTemplates.js';

export const ACTIVITY_TYPE_DIALOG_STEPS = Object.freeze([
  { id: 'question-1', title: 'Frage 1', description: 'Datumsvorschau' },
  { id: 'question-2', title: 'Frage 2', description: 'Grunddaten und Dienstart' },
  { id: 'question-3', title: 'Frage 3', description: 'Verantwortlichenlogik' },
  { id: 'question-4', title: 'Frage 4', description: 'Zeitraum und Pause' },
  { id: 'question-5', title: 'Frage 5', description: 'Stunden und Folgefragen' },
  { id: 'follow-up', title: 'Folgefragen', description: 'Zusatzfunktionen und Sichtbarkeit' },
]);

const DEFAULT_ENTRY_NAME = 'Neue Tätigkeit';

function normalizeDraft(draft = {}) {
  const mergedDraft = {
    ...createEmptyActivityTypeDraft(),
    ...draft,
  };

  const resolvedConfig = resolveActivityTypeConfig(mergedDraft, mergedDraft.name || DEFAULT_ENTRY_NAME);

  return {
    ...mergedDraft,
    resolvedConfig,
    entryTypeName: String(mergedDraft.name || '').trim() || DEFAULT_ENTRY_NAME,
    isDayService: typeof mergedDraft.isDayService === 'boolean' ? mergedDraft.isDayService : Boolean(resolvedConfig.isDayService),
    supportsA3Za3: typeof mergedDraft.supportsA3Za3 === 'boolean' ? mergedDraft.supportsA3Za3 : Boolean(resolvedConfig.supportsA3Za3),
    supportsTGroup: typeof mergedDraft.supportsTGroup === 'boolean' ? mergedDraft.supportsTGroup : Boolean(resolvedConfig.supportsTGroup),
    supportsHonorar: typeof mergedDraft.supportsHonorar === 'boolean' ? mergedDraft.supportsHonorar : Boolean(resolvedConfig.supportsHonorar),
  };
}

function getCodeGroupOptions(normalizedDraft) {
  const timeSelectionMode = getDialogTimeSelectionMode(normalizedDraft);
  if (!normalizedDraft.isDayService || timeSelectionMode !== 'full-day-service') {
    return [];
  }

  if (normalizedDraft.timeEntryMode === 'a3-za3') {
    return [
      { id: 'A', label: 'A3/zA3' },
    ];
  }

  const options = [];
  if (normalizedDraft.supportsTGroup) {
    options.push({ id: 'T', label: 'TE1/2' });
  }
  if (normalizedDraft.supportsHonorar) {
    options.push({ id: 'HONORAR', label: 'Honorar' });
  }

  if (!options.length) {
    return [
      { id: 'T', label: 'TE1/2' },
    ];
  }

  return options;
}

function getPreviewQuestionNumber(baseNumber, suffix = '') {
  return suffix ? `${baseNumber}${suffix}` : String(baseNumber);
}

export function getDialogServiceKind(draft = {}) {
  return normalizeDraft(draft).isDayService ? 'day-service' : '24h-service';
}

export function getDialogTimeSelectionMode(draft = {}) {
  const normalizedDraft = normalizeDraft(draft);
  if (normalizedDraft.timeEntryMode === 'a3-za3' || normalizedDraft.timeEntryMode === 'te1-flex-honorar') {
    return 'full-day-service';
  }

  if (normalizedDraft.timeEntryMode === 'preset-or-custom') {
    return 'predefined';
  }

  return 'individual';
}

export function getDialogQuestion4bMode(draft = {}) {
  const normalizedDraft = normalizeDraft(draft);
  if (getDialogTimeSelectionMode(normalizedDraft) !== 'full-day-service') {
    return 'not-applicable';
  }

  if (normalizedDraft.timeEntryMode === 'a3-za3') {
    return 'auto-a3-za3';
  }

  if (normalizedDraft.supportsTGroup && normalizedDraft.supportsHonorar) {
    return 'visible';
  }

  if (normalizedDraft.supportsHonorar) {
    return 'fixed-honorar';
  }

  return 'fixed-t';
}

export function updateDraftServiceKind(draft = {}, nextKind) {
  const normalizedDraft = normalizeDraft(draft);

  if (nextKind === 'day-service') {
    return {
      ...normalizedDraft,
      isDayService: true,
      timeEntryMode: normalizedDraft.timeEntryMode === 'custom-only' ? 'a3-za3' : normalizedDraft.timeEntryMode,
      pauseMode: 'question',
      scaleWorkHours: true,
      supportsA3Za3: normalizedDraft.supportsA3Za3 || (!normalizedDraft.supportsTGroup && !normalizedDraft.supportsHonorar),
    };
  }

  return {
    ...normalizedDraft,
    isDayService: false,
    timeEntryMode: normalizedDraft.timeEntryMode === 'a3-za3' || normalizedDraft.timeEntryMode === 'te1-flex-honorar'
      ? 'preset-or-custom'
      : normalizedDraft.timeEntryMode,
    pauseMode: normalizedDraft.timeEntryMode === 'preset-or-custom' ? normalizedDraft.pauseMode : 'hidden-no-deduction',
    scaleWorkHours: false,
    supportsA3Za3: false,
    supportsTGroup: false,
    supportsHonorar: false,
  };
}

function getPreferredFullDayMode(normalizedDraft) {
  if (normalizedDraft.supportsTGroup || normalizedDraft.supportsHonorar) {
    return 'te1-flex-honorar';
  }

  return 'a3-za3';
}

export function updateDraftTimeSelectionMode(draft = {}, nextMode) {
  const normalizedDraft = normalizeDraft(draft);
  if (nextMode === 'full-day-service') {
    return {
      ...normalizedDraft,
      isDayService: true,
      timeEntryMode: getPreferredFullDayMode(normalizedDraft),
      pauseMode: 'question',
      scaleWorkHours: true,
      supportsA3Za3: normalizedDraft.supportsA3Za3 || (!normalizedDraft.supportsTGroup && !normalizedDraft.supportsHonorar),
    };
  }

  if (nextMode === 'predefined') {
    return {
      ...normalizedDraft,
      timeEntryMode: 'preset-or-custom',
      scaleWorkHours: false,
    };
  }

  return {
    ...normalizedDraft,
    timeEntryMode: 'custom-only',
    scaleWorkHours: false,
  };
}

export function updateDraftQuestion4bMode(draft = {}, nextMode) {
  const normalizedDraft = normalizeDraft(draft);
  if (normalizedDraft.timeEntryMode !== 'te1-flex-honorar') {
    return normalizedDraft;
  }

  if (nextMode === 'visible') {
    return {
      ...normalizedDraft,
      supportsTGroup: true,
      supportsHonorar: true,
    };
  }

  if (nextMode === 'fixed-honorar') {
    return {
      ...normalizedDraft,
      supportsTGroup: false,
      supportsHonorar: true,
    };
  }

  return {
    ...normalizedDraft,
    supportsTGroup: true,
    supportsHonorar: false,
  };
}

export function buildActivityTypeDialogPreview(draft = {}) {
  const normalizedDraft = normalizeDraft(draft);
  const timeSelectionMode = getDialogTimeSelectionMode(normalizedDraft);
  const question4bMode = getDialogQuestion4bMode(normalizedDraft);
  const entryRule = getLogbookEntryTypeRule(normalizedDraft.entryTypeName, normalizedDraft);
  const responsibleQuestion = getResponsibleQuestion(normalizedDraft.entryTypeName, normalizedDraft);
  const timeQuestion = getTimeQuestion(normalizedDraft.entryTypeName, normalizedDraft);
  const codeGroups = getCodeGroupOptions(normalizedDraft);
  const showCodeQuestion = codeGroups.length > 1;
  const exampleDuration = normalizedDraft.isDayService ? 9.5 : 7.5;
  const noBreakCompensation = entryRule.showPauseQuestion
    ? getNoBreakCompensationPauseHours(exampleDuration)
    : 0;
  const previewItems = [
    {
      id: 'question-1',
      number: getPreviewQuestionNumber(1),
      stepId: 'question-1',
      title: 'Datumsfrage',
      question: '1. Wann hat die Übung/der Einsatz statt gefunden?',
      answerPreview: 'Heute oder Kalenderdatum auswählen',
      status: 'immer sichtbar',
      details: ['Die Datumsfrage bleibt für alle Tätigkeiten identisch.'],
    },
    {
      id: 'question-2',
      number: getPreviewQuestionNumber(2),
      stepId: 'question-2',
      title: 'Tätigkeit und Dienstart',
      question: '2. Wähle die Art der Tätigkeit aus',
      answerPreview: `${normalizedDraft.entryTypeName} (${normalizedDraft.isDayService ? 'Tagesdienst' : '24h-Dienst'})`,
      status: 'immer sichtbar',
      details: [
        `Sortierung: ${normalizedDraft.sortOrder}`,
        `Aktiv: ${normalizedDraft.isActive ? 'Ja' : 'Nein'}`,
      ],
    },
    {
      id: 'question-3',
      number: getPreviewQuestionNumber(3),
      stepId: 'question-3',
      title: 'Verantwortlicher',
      question: responsibleQuestion,
      answerPreview: entryRule.autoResponsibleCurrentUser ? 'Automatisch aktueller Nutzer' : 'Nutzer wählt Verantwortlichen aus',
      status: 'immer sichtbar',
      details: [
        `Fragemodus: ${normalizedDraft.responsibleQuestionMode}`,
        normalizedDraft.responsibleQuestionText
          ? `Eigener Text aktiv: ${normalizedDraft.responsibleQuestionText}`
          : 'Standardtext aus Wizard-Regeln',
      ],
    },
    {
      id: 'question-4',
      number: getPreviewQuestionNumber(4),
      stepId: 'question-4',
      title: 'Zeitraum',
      question: timeQuestion,
      answerPreview: timeSelectionMode === 'full-day-service'
        ? 'Vollständiger Tagesdienst'
        : timeSelectionMode === 'predefined'
        ? 'Vordefinierter Zeitraum plus individuelle Alternative'
        : 'Individueller Zeitraum',
      status: 'immer sichtbar',
      details: [
        `Technischer Modus: ${normalizedDraft.timeEntryMode}`,
        timeSelectionMode === 'full-day-service'
          ? 'Vollständiger Tagesdienst nutzt die hinterlegte Tagesdienst-Logik.'
          : (timeSelectionMode === 'predefined'
            ? 'Im Wizard erscheint ein Standardzeitraum und zusätzlich die freie Eingabe.'
            : 'Im Wizard werden direkt Von/Bis-Felder eingeblendet.'),
        entryRule.showPauseQuestion
          ? `Tagesdienst-Pausenlogik aktiv: Beispiel ohne Pause bei ${exampleDuration}h => ${noBreakCompensation}h Abzug`
          : 'Keine Pausenfrage und keine Pausenabzuege',
        question4bMode === 'auto-a3-za3'
          ? 'Frage 4b bleibt ausgeblendet. A3 oder zA3 werden später anhand der Dienstzeit automatisch abgeleitet.'
          : (showCodeQuestion
            ? `Frage 4b wird als Folgefrage eingeblendet: ${codeGroups.map((item) => item.label).join(' oder ')}`
            : (timeSelectionMode === 'full-day-service'
              ? `Frage 4b ist ausgeblendet. Feste Vorgabe: ${codeGroups[0]?.label || 'TE1/2'}`
              : 'Frage 4b ist für diese Zeitraum-Art nicht relevant.')),
      ],
    },
  ];

  if (normalizedDraft.isDayService && timeSelectionMode === 'individual') {
    previewItems.push({
      id: 'question-4c',
      number: getPreviewQuestionNumber(4, 'c'),
      stepId: 'question-4',
      title: 'A3-Empfehlung',
      question: '4c. Soll der Eintrag auf einen vollständigen Tagesdienst umgestellt werden?',
      answerPreview: 'Konditionale Folgefrage bei individuellem Zeitraum nahe an einem vollständigen Tagesdienst',
      status: 'konditional',
      details: ['Erscheint nur, wenn der individuelle Zeitraum fachlich nahe an einem vollen Tagesdienst liegt.'],
    });
  }

  previewItems.push({
    id: 'question-5',
    number: getPreviewQuestionNumber(5),
    stepId: 'question-5',
    title: 'Stunden',
    question: '5. Wie viele Höhenretterstunden wurden absolviert?',
    answerPreview: entryRule.autoHoursZero ? 'Automatisch 0 Stunden' : 'Frage wird eingeblendet',
    status: 'immer sichtbar',
    details: [
      `Schlagworte: ${normalizedDraft.keywordMode === 'hidden' ? 'ausgeblendet' : normalizedDraft.keywordMode === 'required' ? 'erforderlich' : 'optional'}`,
      `Dienstzeit-Label: ${entryRule.serviceDurationLabel}`,
    ],
  });

  if (normalizedDraft.keywordMode !== 'hidden') {
    previewItems.push({
      id: 'question-5a',
      number: getPreviewQuestionNumber(5, 'a'),
      stepId: 'question-5',
      title: 'Schlagworte',
      question: `5a. Welche Schlagwörter beschreiben den Inhalt der Veranstaltung?${normalizedDraft.keywordMode === 'required' ? ' (Pflichtfeld)' : ''}`,
      answerPreview: normalizedDraft.keywordMode === 'required' ? 'Mindestens ein Schlagwort erforderlich' : 'Schlagwörter optional',
      status: 'konditional',
      details: ['Der Schlagwort-Block folgt direkt nach Frage 5.'],
    });
  }

  if (!normalizedDraft.isDayService && timeSelectionMode !== 'full-day-service' && entryRule.showPauseQuestion) {
    previewItems.push({
      id: 'question-6',
      number: getPreviewQuestionNumber(6),
      stepId: 'follow-up',
      title: 'Pausenfrage',
      question: '6. Wurden Pausen gemacht?',
      answerPreview: 'Ja / Nein',
      status: 'konditional',
      details: ['Die Frage erscheint nur, wenn Zeitraum und Dienstzeit die Pausenlogik relevant machen.'],
    });
  }

  if (showCodeQuestion) {
    previewItems.push({
      id: 'question-7-code',
      number: getPreviewQuestionNumber(7),
      stepId: 'follow-up',
      title: 'Dienstkürzel',
      question: '7. Welches Dienstkürzel soll genutzt werden?',
      answerPreview: codeGroups.map((item) => item.label).join(' / '),
      status: 'konditional',
      details: ['Die Auswahl erscheint nur, wenn mehrere Kürzelgruppen gleichzeitig erlaubt sind.'],
    });
  }

  previewItems.push({
    id: 'question-copy',
    number: getPreviewQuestionNumber(showCodeQuestion ? 8 : 7),
    stepId: 'follow-up',
    title: 'Weitere Logbücher',
    question: 'Soll der Eintrag in andere Logbücher übernommen werden?',
    answerPreview: 'Ja / Nein',
    status: 'konditional',
    details: ['Die Frage wird nur für Nutzer mit Berechtigung zur Mehrfachübernahme eingeblendet.'],
  });

  previewItems.push({
    id: 'question-incident',
    number: getPreviewQuestionNumber(showCodeQuestion ? 9 : 8),
    stepId: 'follow-up',
    title: 'Besondere Vorkommnisse',
    question: 'Gab es besondere Vorkommnisse?',
    answerPreview: 'Ja / Nein',
    status: 'konditional',
    details: ['Bei Ja folgt der Rückmeldungsblock mit Detailfeldern und Anlagen.'],
  });

  if (normalizedDraft.pdfProtocolMode === 'ask') {
    previewItems.push({
      id: 'question-pdf',
      number: getPreviewQuestionNumber(showCodeQuestion ? 10 : 9),
      stepId: 'follow-up',
      title: 'PDF-Protokoll',
      question: 'Soll ein Stundenprotokoll im PDF Format erstellt werden?',
      answerPreview: 'Ja / Nein',
      status: 'konditional',
      details: ['Das PDF-Protokoll erscheint nur, wenn die Tätigkeit die PDF-Abfrage aktiviert.'],
    });
  }

  return previewItems;
}
